import { useCallback, useState } from 'react';
import { exportWithIframes } from './multiplePagesUtils';
import { exportElements } from './singlePageUtils';
import { MultiplePagesExportOptions, MultiplePagesExportResult, PdfExportOptions, PdfExportResult, UsePdfExportOptions, UsePdfExportReturn } from './types';

/**
 * React hook for easy PDF export integration
 * @param options - Configuration options for PDF export
 * @returns Object with export function and state
 */
export const usePdfExport = (options: UsePdfExportOptions = {}): UsePdfExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [isMultiplePagesExporting, setIsMultiplePagesExporting] = useState(false);
  const [lastExportResult, setLastExportResult] = useState<PdfExportResult | null>(null);
  const [lastMultiplePagesExportResult, setLastMultiplePagesExportResult] = useState<MultiplePagesExportResult | null>(null);

  const { showLoading = true, onExportStart, onExportComplete, onExportError, ...pdfOptions } = options;

  const exportSinglePageAsPdf = useCallback(
    async (
      dataRefNames: string[],
      customOptions: Partial<PdfExportOptions> = {}
    ): Promise<PdfExportResult> => {
      try {
        if (showLoading) {
          setIsExporting(true);
        }
        onExportStart?.();

        const mergedOptions = { ...pdfOptions, ...customOptions };
        const result = await exportElements(dataRefNames, mergedOptions);

        setLastExportResult(result);
        onExportComplete?.(result);

        if (!result.success && result.error) {
          onExportError?.(result.error);
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown PDF export error occurred';
        const result: PdfExportResult = { success: false, error: errorMessage };

        setLastExportResult(result);
        onExportError?.(errorMessage);
        onExportComplete?.(result);

        return result;
      } finally {
        if (showLoading) {
          setIsExporting(false);
        }
      }
    },
    [pdfOptions, showLoading, onExportStart, onExportComplete, onExportError]
  );

  const exportMultiplePagesAsPdf = useCallback(
    async (multiplePagesOptions: MultiplePagesExportOptions): Promise<MultiplePagesExportResult> => {
      try {
        if (showLoading) {
          setIsMultiplePagesExporting(true);
        }
        onExportStart?.();

        const mergedOptions = { ...pdfOptions, ...multiplePagesOptions };
        const result = await exportWithIframes(mergedOptions);

        setLastMultiplePagesExportResult(result);
        onExportComplete?.(result);

        if (!result.success && result.error) {
          onExportError?.(result.error);
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown multiple pages PDF export error occurred';
        const result: MultiplePagesExportResult = { success: false, error: errorMessage };

        setLastMultiplePagesExportResult(result);
        onExportError?.(errorMessage);
        onExportComplete?.(result);

        return result;
      } finally {
        if (showLoading) {
          setIsMultiplePagesExporting(false);
        }
      }
    },
    [pdfOptions, showLoading, onExportStart, onExportComplete, onExportError]
  );

  return {
    exportSinglePageAsPdf,
    exportMultiplePagesAsPdf,
    isExporting,
    isMultiplePagesExporting,
    lastExportResult,
    lastMultiplePagesExportResult,
  };
};
